import React from 'react';
import { Popover, Progress } from 'antd';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { CampaignResponse } from '@models/campaign';
import { ItemResponse } from '@models/order/items/response';
import { orderItemsSelector } from '@redux/slices/order';
import {
    computeAtingirMetaBonusLines,
    evaluateAtingirMetaCondition,
    getMetaLineValue,
} from '@domain/campaign/strategies/desconto-atingir-meta.strategy';
import { formatMoney } from '@utils/money';
import { translate } from '@components/i18n';

interface Props {
    campaign: CampaignResponse;
}

const SIZE = 36;
const STROKE = 1.5;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const COLOR_ACTIVE = '#1890ff';
const COLOR_MET = '#52c41a';
const COLOR_TRACK = '#e8e8e8';

interface ResultRow {
    numeroItem: string;
    description: string;
    meta: number;
    achieved: number;
}

const buildResultRows = (
    campaign: CampaignResponse,
    items: ItemResponse[],
): ResultRow[] => {
    return (campaign.resultados ?? [])
        .filter((r) => !!r.numeroItem)
        .map((r) => {
            const item = items.find(
                (i) =>
                    (i.number ?? '').toLowerCase() ===
                    r.numeroItem.toLowerCase(),
            );
            const gross = Number(item?.valorGross) || 0;
            const percentualCota = Number(r.percentualCota) || 0;
            return {
                numeroItem: r.numeroItem,
                description: item?.description ?? r.numeroItem,
                meta: gross * (1 + percentualCota / 100),
                achieved: getMetaLineValue(item),
            };
        });
};

export const CampaignQuotaButton: React.FC<Props> = ({ campaign }) => {
    const order = useSelector(orderItemsSelector);
    const simbolo = order.selectedMoeda?.simbolo;
    const items = order.availableItems ?? [];

    const { minTotal, achievedTotal, isMet } = evaluateAtingirMetaCondition(
        campaign,
        items,
    );

    const percentual =
        minTotal > 0 ? Math.min(100, (achievedTotal / minTotal) * 100) : (isMet ? 100 : 0);
    const color = isMet ? COLOR_MET : COLOR_ACTIVE;
    const dashOffset = CIRCUMFERENCE * (1 - percentual / 100);

    const resultRows = buildResultRows(campaign, items);
    const bonusLines = computeAtingirMetaBonusLines(campaign, items);

    const content = (
        <div style={{ minWidth: 320 }}>
            <SummaryRow>
                <Label>{translate('general.minToReach')}:</Label>
                <Value>{formatMoney(simbolo, minTotal)}</Value>
            </SummaryRow>
            <SummaryRow>
                <Label>{translate('general.achieved')}:</Label>
                <Value>{formatMoney(simbolo, achievedTotal)}</Value>
            </SummaryRow>
            <SummaryRow>
                <Label>{translate('general.progress')}:</Label>
                <Value>{percentual.toFixed(1)}%</Value>
            </SummaryRow>
            <Progress
                percent={percentual}
                showInfo={false}
                strokeColor={color}
                style={{ marginTop: 4, marginBottom: 8 }}
            />
            {resultRows.length > 0 && (
                <ResultsTable>
                    <ResultHeader>
                        <ItemCell>{translate('columns.item')}</ItemCell>
                        <NumCell>{translate('general.minToReach')}</NumCell>
                        <NumCell>{translate('general.achieved')}</NumCell>
                        <NumCell>%</NumCell>
                    </ResultHeader>
                    {resultRows.map((r) => {
                        const rowPercent =
                            r.meta > 0
                                ? Math.min(100, (r.achieved / r.meta) * 100)
                                : r.achieved > 0 ? 100 : 0;
                        const rowMet = r.meta === 0 ? r.achieved > 0 : r.achieved >= r.meta;
                        return (
                            <ResultRowEl key={r.numeroItem}>
                                <ItemCell title={r.description}>
                                    {r.description}
                                </ItemCell>
                                <NumCell>
                                    {formatMoney(simbolo, r.meta)}
                                </NumCell>
                                <NumCell
                                    style={{
                                        color: rowMet ? COLOR_MET : '#262626',
                                    }}
                                >
                                    {formatMoney(simbolo, r.achieved)}
                                </NumCell>
                                <NumCell
                                    style={{
                                        color: rowMet ? COLOR_MET : '#262626',
                                        fontWeight: 600,
                                    }}
                                >
                                    {rowPercent.toFixed(0)}%
                                </NumCell>
                            </ResultRowEl>
                        );
                    })}
                </ResultsTable>
            )}
            {bonusLines.length > 0 && (
                <ResultsTable>
                    <SummaryRow>
                        <Label>{translate('general.plannedBonification')}:</Label>
                    </SummaryRow>
                    {bonusLines.map((b) => (
                        <ResultRowEl
                            key={`bonus-${b.numeroItem}`}
                            style={{ gridTemplateColumns: '1fr auto' }}
                        >
                            <ItemCell title={b.description}>
                                {b.description}
                            </ItemCell>
                            <NumCell style={{ color: COLOR_MET, fontWeight: 600 }}>
                                +{b.bonusQty} × {b.bonusMultiplo}
                            </NumCell>
                        </ResultRowEl>
                    ))}
                </ResultsTable>
            )}
        </div>
    );

    return (
        <Popover
            title={campaign.nome}
            content={content}
            trigger="hover"
            placement="bottom"
        >
            <ButtonWrapper>
                <svg width={SIZE} height={SIZE}>
                    <circle
                        cx={SIZE / 2}
                        cy={SIZE / 2}
                        r={RADIUS}
                        fill="none"
                        stroke={COLOR_TRACK}
                        strokeWidth={STROKE}
                    />
                    <circle
                        cx={SIZE / 2}
                        cy={SIZE / 2}
                        r={RADIUS}
                        fill="none"
                        stroke={color}
                        strokeWidth={STROKE}
                        strokeDasharray={CIRCUMFERENCE}
                        strokeDashoffset={dashOffset}
                        strokeLinecap="round"
                        transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
                        style={{ transition: 'stroke-dashoffset 0.3s ease' }}
                    />
                    <text
                        x="50%"
                        y="50%"
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize={11}
                        fontWeight={600}
                        fill={color}
                    >
                        {Math.round(percentual)}%
                    </text>
                </svg>
            </ButtonWrapper>
        </Popover>
    );
};

const ButtonWrapper = styled.div`
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: ${SIZE}px;
    height: ${SIZE}px;
    background: transparent;
`;

const SummaryRow = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: 2px;
`;

const Label = styled.span`
    color: #595959;
    margin-right: 12px;
`;

const Value = styled.span`
    font-weight: 600;
    color: #262626;
`;

const ResultsTable = styled.div`
    border-top: 1px solid #f0f0f0;
    padding-top: 6px;
`;

const ResultHeader = styled.div`
    display: grid;
    grid-template-columns: 1.4fr 1fr 1fr 0.6fr;
    gap: 8px;
    font-size: 11px;
    color: #8c8c8c;
    text-transform: uppercase;
    margin-bottom: 4px;
`;

const ResultRowEl = styled.div`
    display: grid;
    grid-template-columns: 1.4fr 1fr 1fr 0.6fr;
    gap: 8px;
    font-size: 12px;
    padding: 2px 0;
`;

const ItemCell = styled.span`
    color: #262626;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const NumCell = styled.span`
    color: #262626;
    text-align: right;
    font-variant-numeric: tabular-nums;
`;
